import { Component, OnInit, inject, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Campaign, CampaignAdminRequest, CampaignLocationFilter, CampaignsCampaignAdminApiClient, CampaignsCampaignLocationFilterAdminApiClient, CampaignsOutboundEventConfigurationApiClient } from 'src/app/api/api-campaigns.service.generated';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { BannerImage } from '../_/campaign-banner-image/campaign-banner-image.component';
import { CampaignBannerComponent } from '../_/campaign-banner/campaign-banner.component';
import { CampaignBaseEditComponent } from '../_/campaign-base-edit/campaign-base-edit.component';
import { CampaignLocationFiltersComponent } from '../_/campaign-location-filters/campaign-location-filters.component';
import { StaffPointStrategyReceiptComponent } from './staff-point-strategy-receipt/staff-point-strategy-receipt.component';
import { StaffPointStrategyComponent } from './staff-point-strategy/staff-point-strategy.component';
import { PERMISSION } from 'src/app/const/permission';
import { CampaignMediaComponent } from '../_/campaign-media/campaign-media.component';
import { CampaignOutboundEventsComponent } from '../campaign-outbound-events/campaign-outbound-events.component';
import { PageContainerComponent, BreadcrumbsComponent, ButtonComponent } from 'wecheer-common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { AsyncPipe, JsonPipe } from '@angular/common';
import { ScreenService } from 'src/app/common/services/screen.service';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { MatTabsModule } from '@angular/material/tabs';
import { FlexLayoutModule } from '@angular/flex-layout';


@Component({
  selector: 'app-staff-point-campaign-edit',
  templateUrl: './staff-point-campaign-edit.component.html',
  styleUrls: ['./staff-point-campaign-edit.component.scss'],
  imports: [
    CampaignBaseEditComponent,
    CampaignBannerComponent,
    StaffPointStrategyComponent,
    CampaignLocationFiltersComponent,
    StaffPointStrategyReceiptComponent,
    CampaignMediaComponent,
    CampaignOutboundEventsComponent,
    MatCardModule,
    MatIconModule,
    MatTabsModule,
    FlexLayoutModule,
    HasAnyPermissionDirective,
    JsonPipe,
    AsyncPipe,
    PageContainerComponent,
    BreadcrumbsComponent,
    ButtonComponent
]
})
export class StaffPointCampaignEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private campaignLocationFiltersApi = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private outboundEventsApi = inject(CampaignsOutboundEventConfigurationApiClient);
  screen = inject(ScreenService);
  appVersion = inject(AppVersionService);

  readonly campaignBase = viewChild<CampaignBaseEditComponent>('campaignBase');
  readonly campaignBanner = viewChild<CampaignBannerComponent>('campaignBanner');
  readonly campaignStrategy = viewChild<StaffPointStrategyComponent>('campaignStrategy');
  readonly campaignLocationFilters = viewChild<CampaignLocationFiltersComponent>('campaignLocationFilters');
  readonly campaignStrategyReceipt = viewChild<StaffPointStrategyReceiptComponent>('campaignStrategyReceipt');
  readonly campaignOutboundEvents = viewChild<CampaignOutboundEventsComponent>('campaignOutboundEvents');

  editModel: CampaignAdminRequest;

  campaign: Campaign;

  campaignReplay = new BehaviorSubject<Campaign>(undefined);

  campaignId: string;
  advertiserId: string;
  originalCampaignId: string;
  clonedLocationFilters: CampaignLocationFilter;

  isLoading: boolean;
  isAdding: boolean;
  isCloning: boolean;

  viewMode: 'scheme' | 'receipt' | 'locations' | 'json' | 'media' | 'banner' | '' = 'scheme';
  readonly permissions = PERMISSION;


  constructor() {

    this.isAdding = this.route.snapshot.params.campaignId === 'new';
    this.campaignId = this.isAdding ? undefined : this.route.snapshot.params.campaignId;
    this.advertiserId = this.route.snapshot.params.advertiserId;
    this.isCloning = this.route.snapshot.queryParams.clone === 'true';
    this.originalCampaignId = this.campaignId;
  }

  async ngOnInit() {
    try {
      this.isLoading = true;

      if (this.isCloning) {
        await this.cloneCampaign();
      } else {
        this.campaign = this.campaignId
          ? await lastValueFrom(this.campaignsApi.getCampaign(this.campaignId))
          : new Campaign({ advertiserId: this.advertiserId });
      }
    } catch (err: any) {
    } finally {
      this.isLoading = false;
    }

    // Load location filters - following punch-card pattern
    if (this.isCloning) {
      // When cloning, location filters are already loaded in cloneCampaign()
      this.campaignLocationFilters().setFormValue(this.clonedLocationFilters);
    } else {
      const clf = this.campaignId
        ? await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.campaignId))
        : new CampaignLocationFilter({ campaignId: this.campaignId, filters: [] });
      this.campaignLocationFilters().setFormValue(clf);
    }

    this.campaignReplay.next(this.campaign);

    setTimeout(() => {
      this.syncBanners();
    }, 500);
  }

  syncBanners() {
    // sync baseCampaign main banner with banner slider tab (can be edited from 2 places)
    this.campaignBase().mainBannerChanged.subscribe(banner => { this.campaignBanner().setMainBanner(banner); });
    this.campaignBanner().mainBannerChanged.subscribe(banner => { this.campaignBase().setMainBanner(banner); });

    this.campaignBanner().setBanners(this.campaign.strategy?.banner);

    if (!this.campaign.strategy?.banner) {
      // old campaings have banner set on campaign.photoBannerUrl prop -> add it to the banner list
      if (this.campaign.photoBannerUrl)
        this.campaignBanner().setMainBanner(new BannerImage({ photoUrl: this.campaign.photoBannerUrl }));
    }
    else {
      // new campaign will have banner list -> set it also on the campaign base main banner
      const strategyBanner = this.campaign.strategy.banner;
      if (strategyBanner && strategyBanner.url1)
        this.campaignBase().setMainBanner(new BannerImage({ photoUrl: strategyBanner.url1 }))
    }
  }

  private async cloneCampaign() {
    const originalCampaign = await lastValueFrom(this.campaignsApi.getCampaign(this.originalCampaignId));

    // Copy all data from original campaign, reset only essential fields
    this.campaign = new Campaign({
      ...originalCampaign,
      id: undefined,
      campaignId: undefined,
      dateUpdated: undefined,
      title: originalCampaign.title ? `Clone of ${originalCampaign.title}` : undefined,
      advertiserId: this.advertiserId
    });

    // When cloning, treat as adding a new campaign
    this.isAdding = true;
    this.campaignId = undefined;

    // Load related data
    const clf = await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.originalCampaignId));

    // Store location filters to be set in setTimeout when viewChild is ready
    this.clonedLocationFilters = clf;
  }

  forceRefresh() {
    const c = this.campaign;
    this.campaign = null;
    this.isLoading = true;
    setTimeout(() => {
      this.campaign = c;
      this.isLoading = false;
    }, 500);
  }


  async onSave() {
    this.campaignOutboundEvents()?.markAsTouched();

    const request = this.campaignBase().getFormValue();
    if (!request) return;

    let strategy = this.campaignStrategy().getFormValue();
    if (!strategy) return;
    if (!this.campaignStrategyReceipt().getFormValue(strategy)) return;

    if (!this.campaignOutboundEvents().isValid()) return;

    request.campaignId = this.campaignId;
    request.advertiserId = this.advertiserId;
    request.strategy = strategy;
    request.strategy.banner = this.campaignBanner().getBanners();

    strategy.allowDuplicates = request.allowDuplicates;               // set this prop on campaign and also on strategy (to be fixed later)


    request.campaignLocationFilter = this.campaignLocationFilters().getFormValue();

    this.isLoading = true;
    try {
      const savedCampaign = await lastValueFrom(this.campaignsApi.createCampaign(request));

      const outboundEventsConfig = this.campaignOutboundEvents()?.getFormValue();
      if (outboundEventsConfig && savedCampaign?.campaignId) {
        try {
          await lastValueFrom(this.outboundEventsApi.updateCampaignConfig(savedCampaign.campaignId, outboundEventsConfig));
        } catch (err) {
          console.warn('Failed to save outbound events configuration:', err);
        }
      }

      this.snackBar.open(`Campaign ${request.title} was saved`, null, { duration: 3000 });
      this.router.navigate(['advertisers', this.advertiserId]);

    } catch (err: any) {
      if (err.status === 400 && err.response)
        new MessageDialogModel(err.response).show(this.dialog);
    } finally {
      this.isLoading = false;
    }
  }

  refAssignObj(ref: any, obj: any) {
    if (!ref) return;
    for (const key of Object.keys(ref))
      delete ref[key];

    if (!obj) return;
    for (const key of Object.keys(obj))
      ref[key] = obj[key];
  }

}
